import React, { useState, useRef, useId } from 'react';

/**
 * A "?" next to a field label. Hover or focus opens a card explaining the field.
 *
 * Every org-config field needs an explanation, and the previous approach — a line
 * of grey text under the input — only ever got filled in for four fields, because
 * it competes with the input for space. A tip that stays out of the way until
 * asked for can afford to say the three things that actually help: what the field
 * does, what breaks when it is wrong, and a real example from an existing org.
 *
 * Keyboard-reachable on purpose: it is a <button>, opens on focus, and closes on
 * Escape, so the explanation is not mouse-only.
 */
export default function HelpTip({ what, breaks, example, placement = 'right' }) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  const id = useId();

  if (!what) return null;

  const show = () => {
    clearTimeout(closeTimer.current);
    setOpen(true);
  };
  // Small delay so moving the pointer from the "?" onto the card does not close it.
  const hide = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  return (
    <span
      style={{ position: 'relative', display: 'inline-flex', marginLeft: '5px', verticalAlign: 'middle' }}
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      <button
        type="button"
        aria-label="What is this?"
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        onFocus={show}
        onBlur={hide}
        onClick={() => setOpen(o => !o)}
        onKeyDown={e => { if (e.key === 'Escape') setOpen(false); }}
        style={{
          width: '15px', height: '15px', borderRadius: '50%', padding: 0,
          border: '1px solid #ced4da', background: open ? '#1e3a5f' : '#fff',
          color: open ? '#fff' : '#868e96', fontSize: '10px', fontWeight: 700,
          lineHeight: '13px', cursor: 'pointer', display: 'inline-flex',
          alignItems: 'center', justifyContent: 'center',
        }}
      >
        ?
      </button>

      {open && (
        <span
          id={id}
          role="tooltip"
          style={{
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            [placement === 'left' ? 'right' : 'left']: '22px',
            zIndex: 50,
            width: '300px',
            padding: '10px 12px',
            background: '#fff',
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            boxShadow: '0 6px 20px rgba(0,0,0,0.13)',
            fontSize: '12px',
            lineHeight: 1.5,
            color: '#343a40',
            textAlign: 'left',
            whiteSpace: 'normal',
            fontWeight: 400,
          }}
        >
          <span style={{ display: 'block' }}>{what}</span>
          {breaks && (
            <span style={{ display: 'block', marginTop: '6px', color: '#c92a2a' }}>
              <strong>If it is wrong:</strong> {breaks}
            </span>
          )}
          {example && (
            <span style={{ display: 'block', marginTop: '6px', color: '#6c757d' }}>
              <strong>Example:</strong>{' '}
              <code style={{ fontSize: '11px', wordBreak: 'break-word' }}>{example}</code>
            </span>
          )}
        </span>
      )}
    </span>
  );
}
